/* La Sura - shared helpers and colours for the cake preview drawing. Split
   from lasura-cake-draw.js to keep each script under Theme Check's 10 KB
   limit. Load this first; the toppings are lasura-cake-draw-toppings.js. */
(() => {
  /* The illustration's own colours: ingredients, board and props, not the
     brand. Kept in one place so each can carry its brand-check exception. */
  const P = {
    white: '#FFFFFF', /* brand-check allow: highlight in the cake illustration */
    crumbShadow: '#2A1A10', /* brand-check allow: cylinder shading in the cake illustration */
    frost: '#F7F0E3', /* brand-check allow: default icing in the cake illustration */
    sponge: '#E9D1A0', /* brand-check allow: default sponge in the cake illustration */
    filling: '#F3E4C6', /* brand-check allow: default filling in the cake illustration */
    ganache: '#3E2519', /* brand-check allow: chocolate ganache in the cake illustration */
    ceramic: '#FBF6EE', /* brand-check allow: ceramic cake stand in the illustration */
    ceramicShade: '#E8DCCA', /* brand-check allow: ceramic cake stand shading in the illustration */
    ceramicEdge: '#D9CAB2', /* brand-check allow: ceramic cake stand edges and flutes in the illustration */
    rim: '#D6B57E',
    shadow: '#31383D',
    sketch: '#80868B', /* brand-check allow: dashed outline of a sculpted cake sketch */
    sketchText: '#596066', /* brand-check allow: caption inside the sculpted cake sketch */
    outline: '#31383D',
    topper: '#C9A24E', /* brand-check allow: gold acrylic topper in the illustration */
    topperEdge: '#8C6A2A', /* brand-check allow: gold acrylic topper edge in the illustration */
    stick: '#B8913F', /* brand-check allow: topper stick in the illustration */
    pipeLight: '#FFF6EA', /* brand-check allow: piped icing on a dark cake */
    pipeDark: '#5B3A28', /* brand-check allow: piped chocolate icing on a light cake */
    gold: ['#D4AF37', '#E8C766'], /* brand-check allow: gold leaf in the cake illustration */
    sprinkles: ['#E86A7E', '#F2C14E', '#6FB3D9', '#8CC08A', '#B892D6', '#FFFFFF'], /* brand-check allow: sprinkles in the cake illustration */
    raspberry: '#C8363F', /* brand-check allow: berries in the cake illustration */
    blueberry: '#3C4A7D', /* brand-check allow: berries in the cake illustration */
    macarons: ['#F4B8C4', '#BFD8B0', '#F6DB95', '#C9B8E6', '#B8D6EC'], /* brand-check allow: macarons in the cake illustration */
    cream: '#FFF6EA', /* brand-check allow: macaron cream in the cake illustration */
    petals: ['#FBF3F5', '#F4B8C4', '#E9D27A', '#D9C7EE'], /* brand-check allow: edible flowers in the cake illustration */
    flowers: ['#7B5AA6', '#F2C94C', '#E98AA8', '#F7F1EA', '#B48AD3'], /* brand-check allow: the lifelike 3D cake's edible flowers (violas, pansy yellow, rose and white) */
    pollen: '#E7B53C', /* brand-check allow: flower centres in the cake illustration */
    shard: '#3A2218', /* brand-check allow: chocolate shards in the cake illustration */
    strawberry: '#D8323F', /* brand-check allow: strawberries in the cake illustration */
    seed: '#F4D27A', /* brand-check allow: strawberry seeds in the cake illustration */
    leaf: '#5E8C4A', /* brand-check allow: strawberry leaves in the cake illustration */
    cherry: '#8E1426', /* brand-check allow: fresh cherries in the cake illustration */
    cherryStem: '#6E6A2E', /* brand-check allow: cherry stems in the cake illustration */
    meringue: '#FFF8F0', /* brand-check allow: meringue kisses in the cake illustration */
    candle: '#F7F0E3', /* brand-check allow: candle wax in the cake illustration */
    flame: '#F4B63F', /* brand-check allow: candle flame in the cake illustration */
    flameCore: '#FFF3C4', /* brand-check allow: candle flame centre in the cake illustration */
    flameEdge: '#E9782C', /* brand-check allow: candle flame edge in the cake illustration */
    glow: '#FFD27A', /* brand-check allow: candle glow in the cake illustration */
    goldHi: '#FFF1C2', /* brand-check allow: gold highlight in the cake illustration */
    goldLo: '#8C6A1C' /* brand-check allow: gold shade in the cake illustration */
  };

  const f1 = (n) => Math.round(n * 10) / 10;
  const hex = (c, fb) => (/^#[0-9a-f]{6}$/i.test(c || '') ? c : fb);
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const rng = (seed) => {
    let a = seed >>> 0;
    return () => {
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };
  const hash = (s) => [...String(s)].reduce((h, c) => Math.imul(h ^ c.charCodeAt(0), 16777619), 2166136261) >>> 0;
  const shade = (h, amt) => {
    const n = parseInt(h.slice(1), 16), t = amt < 0 ? 0 : 255, p = Math.abs(amt) / 100;
    const m = (v) => Math.round((t - v) * p + v);
    return '#' + ((1 << 24) | (m(n >> 16) << 16) | (m((n >> 8) & 255) << 8) | m(n & 255)).toString(16).slice(1);
  };
  /* Blends two colours, for the preview's transitions between choices. */
  const mix = (a, b, t) => {
    if (!/^#[0-9a-f]{6}$/i.test(a || '') || !/^#[0-9a-f]{6}$/i.test(b || '')) return b;
    const x = parseInt(a.slice(1), 16), y = parseInt(b.slice(1), 16);
    const ch = (sh) => Math.round(((x >> sh) & 255) + (((y >> sh) & 255) - ((x >> sh) & 255)) * t);
    return '#' + ((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1);
  };
  /* One piece of a topping: its own group, so pieces land one after another. */
  const pc = (markup, ms) => `<g style="--lasura-d:${Math.round(ms)}ms">${markup}</g>`;
  const dark = (h) => {
    const n = parseInt(h.slice(1), 16);
    return 0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) < 110;
  };

  /* The cake stand's plate for a cake of radius r: the same stand up to
     8 inch, the scale a size change reads against, then a bigger one with a
     rim to spare, as a bakery would (Matt, 2026-09-28: it looked too small). */
  const plateFor = (r) => f1(Math.max(160, r * 1.12 + 12));

  /* Draws a scene smaller about the stand's foot when its plate would reach
     the frame's edge or the cut slice (k: how far the cake has moved over for
     it), or its highest point (y, a topper or toppings) the frame's top. */
  function fit(scene, cx, foot, plate, y, k) {
    const z = Math.min(1, (cx - 12) / plate, (508 - 103 * k - cx) / plate, y < 12 ? (foot - 12) / (foot - y) : 1);
    const at = `${cx} ${f1(foot)}`;
    return { z, scene: z < 0.999 ? `<g transform="translate(${at}) scale(${f1(z * 1000) / 1000}) translate(-${at.replace(' ', ' -')})">${scene}</g>` : scene };
  }

  window.LasuraCakeParts = { P, f1, hex, esc, rng, hash, shade, mix, pc, dark, plateFor, fit };
})();
